import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { ptErrorMessage } from "../lib/ptI18n";
import PTLanguageToggle from "../components/pt/PTLanguageToggle";

const DOW = {
  nl: ["MA", "DI", "WO", "DO", "VR", "ZA", "ZO"],
  en: ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"],
};

/* Publieke persoonlijke boekingspagina (/pt/boek/:token) — via link van de coach. */
export default function PtLinkBooking() {
  const { token } = useParams();
  const { t, lang } = useT();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [day, setDay] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/pt/link/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => setError(err?.response?.status === 410 ? t("pt.link.invalid") : ptErrorMessage(err?.response?.data?.detail, t)));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/pt/link/${token}/book`, { date: day, time, ...form });
      setDone(data.booking);
    } catch (err) {
      const msg = ptErrorMessage(err?.response?.data?.detail, t) || t("pt.link.bookingFailed");
      if (err?.response?.status === 409) {
        setTime("");
        api.get(`/pt/link/${token}`).then(({ data }) => setInfo(data)).catch(() => {});
      }
      window.alert(msg);
    }
    setBusy(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const firstName = info?.name ? `, ${info.name.split(" ")[0]}` : "";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark" data-testid="pt-link-page">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#F5F3F0]/10">
        <Link to="/pt" className="font-logo text-2xl tracking-tight" data-testid="pt-link-logo">JOH.</Link>
        <div className="flex items-center gap-4">
          <PTLanguageToggle compact />
          <span className="chip text-[#A3A3A3]">{t("pt.link.personalTraining").toUpperCase()}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 md:py-16">
        {error ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center" data-testid="pt-link-error">
            <h1 className="font-display uppercase text-3xl md:text-4xl">{t("pt.link.inactive")}</h1>
            <p className="text-[#DDD6CB] mt-4">{error}</p>
            <p className="chip text-[#4A4A4A] mt-4">{t("pt.link.questions")}</p>
          </div>
        ) : !info ? (
          <div className="chip text-[#A3A3A3]" data-testid="pt-link-loading">{t("pt.common.loading")}</div>
        ) : done ? (
          <div className="border p-10 text-center" style={{ borderColor: "var(--joh-accent)" }} data-testid="pt-link-success">
            <CheckCircle2 size={40} className="mx-auto" style={{ color: "var(--joh-accent)" }} />
            <h1 className="font-display uppercase text-3xl md:text-4xl mt-6">{t("pt.link.success")}</h1>
            <p className="text-[#DDD6CB] mt-4">{t("pt.link.successBody", { date: done.date, time: done.time })}</p>
            <Link to="/pt" className="chip inline-block mt-8 px-6 py-3 border border-[#F5F3F0]/25 text-[#DDD6CB] hover:text-white transition-colors" data-testid="pt-link-success-back">{t("pt.link.back")}</Link>
          </div>
        ) : (
          <>
            <div className="section-tag section-tag-dark mb-4">— {t("pt.link.invite").toUpperCase()}</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl" data-testid="pt-link-title">{t("pt.link.title", { name: firstName })}</h1>
            <p className="text-[#DDD6CB] mt-4">{t("pt.link.intro")}</p>

            <div className="chip text-[#A3A3A3] mt-10 mb-3">{t("pt.link.chooseDay")}</div>
            {info.days.length === 0 ? (
              <div className="chip text-[#4A4A4A] py-4" data-testid="pt-link-nodays">{t("pt.link.noDays")}</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="pt-link-days">
                {info.days.map((d) => (
                  <button type="button" key={d.date} onClick={() => { setDay(d.date); setTime(""); }}
                    className={`chip px-4 py-3 border transition-colors ${day === d.date ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#DDD6CB] hover:text-white"}`}
                    style={day === d.date ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : {}}
                    data-testid={`pt-link-day-${d.date}`}>
                    {DOW[lang][d.weekday]} {d.date.slice(8, 10)}-{d.date.slice(5, 7)}
                  </button>
                ))}
              </div>
            )}

            {day && (
              <>
                <div className="chip text-[#A3A3A3] mt-8 mb-3">{t("pt.link.chooseTime")}</div>
                <div className="flex flex-wrap gap-2" data-testid="pt-link-times">
                  {(info.days.find((d) => d.date === day)?.slots || []).map((slot) => (
                    <button type="button" key={slot} onClick={() => setTime(slot)}
                      className={`chip px-4 py-3 border transition-colors ${time === slot ? "text-[#0A0A0A]" : "border-[#F5F3F0]/20 text-[#DDD6CB] hover:text-white"}`}
                      style={time === slot ? { background: "var(--joh-accent)", borderColor: "var(--joh-accent)" } : {}}
                      data-testid={`pt-link-time-${slot}`}>{slot}</button>
                  ))}
                </div>
              </>
            )}

            {day && time && (
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="pt-link-form">
                <div className="chip text-[#A3A3A3]">{t("pt.link.details")}</div>
                <div className="grid grid-cols-2 gap-4">
                  <input value={form.first_name} onChange={set("first_name")} placeholder={t("pt.common.firstName")} className="editorial-input dark w-full" data-testid="pt-link-first" required />
                  <input value={form.last_name} onChange={set("last_name")} placeholder={t("pt.common.lastName")} className="editorial-input dark w-full" data-testid="pt-link-last" required />
                </div>
                <input type="email" value={form.email} onChange={set("email")} placeholder={t("pt.common.email")} className="editorial-input dark w-full" data-testid="pt-link-email" required />
                <input type="tel" value={form.phone} onChange={set("phone")} placeholder={t("pt.common.phone")} className="editorial-input dark w-full" data-testid="pt-link-phone" required />
                <textarea rows={3} value={form.note} onChange={set("note")} placeholder={t("pt.link.note")} className="editorial-input dark w-full" data-testid="pt-link-note" />
                <button type="submit" disabled={busy} className="btn-light w-full justify-center disabled:opacity-40" data-testid="pt-link-submit">
                  {busy ? t("pt.common.sending") : t("pt.link.submit", { date: day, time })}
                </button>
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
}
